// 근무자 직접 할당 모달
import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Flex,
  Box,
  useToast,
  Spinner,
  IconButton,
  Divider
} from '@chakra-ui/react';
import { ViewIcon, CheckIcon } from '@chakra-ui/icons';
import {
  getSemesterWorkers,
  getActiveAcademicSchedule,
  assignWorkerDirectly,
  getAllSemesters,
  getScheduleApplications
} from 'utils/firestoreUtils';
import { checkScheduleConflict, getDayOfWeek, getDayNameKorean } from 'utils/scheduleUtils';
import { useAuth } from 'contexts/AuthContext';

function WorkerAssignmentModal({ isOpen, onClose, schedule, onAssigned }) {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [availableWorkers, setAvailableWorkers] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [viewingSchedule, setViewingSchedule] = useState(null);
  const [activeSemester, setActiveSemester] = useState(null);

  useEffect(() => {
    if (isOpen && schedule) {
      loadAvailableWorkers();
    }
  }, [isOpen, schedule]);

  const loadAvailableWorkers = async () => {
    if (!schedule) return;

    try {
      setLoading(true);

      // 1. 활성 학기 조회
      const semesters = await getAllSemesters();
      const semester = semesters.find(s => s.isActive);
      
      if (!semester) {
        toast({
          title: '활성 학기 없음',
          description: '현재 활성화된 학기가 없습니다.',
          status: 'warning',
          duration: 3000
        });
        setAvailableWorkers([]);
        setActiveSemester(null);
        return;
      }

      setActiveSemester(semester);

      // 2. 이미 배정된 근무자 조회
      const existingApps = await getScheduleApplications(schedule.id);
      const assignedUserIds = existingApps
        .filter(app => app.status === 'selected' || app.status === 'pending')
        .map(app => app.userId);

      // 3. 해당 학기의 모든 근무자 조회
      const workers = await getSemesterWorkers(semester.id);
      
      // 4. 각 근무자의 학업시간표 조회 및 충돌 체크
      const workersWithSchedule = await Promise.all(
        workers.map(async (worker) => {
          const academicSchedule = await getActiveAcademicSchedule(worker.userId);
          
          // 충돌 체크
          let hasConflict = false;
          let conflictInfo = null;
          
          if (academicSchedule) {
            const conflictResult = checkScheduleConflict(academicSchedule, schedule);
            hasConflict = conflictResult.hasConflict;
            conflictInfo = conflictResult.conflictingClasses;
          }

          return {
            ...worker,
            academicSchedule,
            hasConflict,
            conflictInfo
          };
        })
      );

      // 5. 충돌이 없고 이미 배정되지 않은 근무자만 필터링
      const available = workersWithSchedule.filter(w => 
        !w.hasConflict && !assignedUserIds.includes(w.userId)
      );

      setAvailableWorkers(available);
    } catch (error) {
      console.error('근무자 조회 오류:', error);
      toast({
        title: '조회 실패',
        description: '근무자 목록을 불러오는데 실패했습니다.',
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAssignWorker = async (worker) => {
    if (!window.confirm(`${worker.userName}님을 이 스케줄에 배정하시겠습니까?`)) {
      return;
    }

    try {
      setLoading(true);
      await assignWorkerDirectly(
        schedule.id, 
        worker.userId, 
        currentUser.uid,
        activeSemester?.id
      );
      
      toast({
        title: '배정 완료',
        description: `${worker.userName}님이 배정되었습니다.`,
        status: 'success',
        duration: 3000
      });
      
      onAssigned();
      onClose();
    } catch (error) {
      console.error('배정 오류:', error);
      toast({
        title: '배정 실패',
        description: error.message || '근무자 배정에 실패했습니다.',
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewSchedule = (worker) => {
    setSelectedWorker(worker);
    // academicSchedule이 null이어도 모달을 열어서 "시간표 없음" 메시지 표시
    setViewingSchedule(worker.academicSchedule || {});
  };

  const handleCloseScheduleView = () => {
    setSelectedWorker(null);
    setViewingSchedule(null);
  };

  if (!schedule) return null;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent maxW="900px">
          <ModalHeader>근무자 할당</ModalHeader>
          <ModalCloseButton />
          
          <ModalBody>
            {/* 스케줄 정보 */}
            <Box p={4} bg="blue.50" borderRadius="lg" mb={4}>
              <Text fontWeight="bold" fontSize="lg" mb={2}>
                {schedule.location}
              </Text>
              <Text fontSize="sm" color="gray.700">
                {schedule.workDate} ({getDayNameKorean(getDayOfWeek(schedule.workDate))}요일)
              </Text>
              <Text fontSize="sm" color="gray.700">
                {schedule.startTime} - {schedule.endTime}
              </Text>
              <Flex gap={2} mt={2}>
                <Badge colorScheme="purple">
                  {schedule.confirmedCount || 0} / {schedule.requiredHeadcount}명
                </Badge>
                <Badge colorScheme="orange">시급 {schedule.wageAmount?.toLocaleString()}원</Badge>
              </Flex>
            </Box>

            <Divider mb={4} />

            {/* 근무자 목록 */}
            <Text fontWeight="bold" mb={3}>
              배정 가능한 근무자 ({availableWorkers.length}명)
            </Text>

            {loading ? (
              <Flex justify="center" py={10}>
                <Spinner size="xl" />
              </Flex>
            ) : availableWorkers.length === 0 ? (
              <Box textAlign="center" py={10}>
                <Text color="gray.500">
                  배정 가능한 근무자가 없습니다.
                </Text>
                <Text fontSize="sm" color="gray.400" mt={2}>
                  해당 시간에 수업이 없고 잔여 시간이 있는 근무자가 없습니다.
                </Text>
              </Box>
            ) : (
              <Box overflowY="auto" maxH="400px">
                <Table size="sm" variant="simple">
                  <Thead position="sticky" top={0} bg="white" zIndex={1}>
                    <Tr>
                      <Th>이름</Th>
                      <Th>연락처</Th>
                      <Th>활동 시간</Th>
                      <Th>잔여 시간</Th>
                      <Th>작업</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {availableWorkers.map((worker) => (
                      <Tr key={worker.id}>
                        <Td fontWeight="medium">{worker.userName}</Td>
                        <Td fontSize="sm">{worker.userPhone || worker.userEmail}</Td>
                        <Td>
                          <Flex direction="column" fontSize="xs">
                            <Text color="green.600" fontWeight="bold">
                              완료: {worker.completedHours}시간
                            </Text>
                            <Text color="orange.600">
                              신청 중: {worker.appliedHours}시간
                            </Text>
                          </Flex>
                        </Td>
                        <Td>
                          <Badge 
                            colorScheme={
                              worker.remainingHours > 10 ? 'blue' : 
                              worker.remainingHours > 0 ? 'orange' : 'red'
                            }
                            fontSize="sm"
                          >
                            {worker.remainingHours}시간
                          </Badge>
                          <Text fontSize="xs" color="gray.500" mt={1}>
                            (최대: {worker.maxWorkHours}시간)
                          </Text>
                        </Td>
                        <Td>
                          <Flex gap={1}>
                            <IconButton
                              icon={<ViewIcon />}
                              size="xs"
                              onClick={() => handleViewSchedule(worker)}
                              aria-label="시간표 확인"
                              title="학업시간표 확인"
                            />
                            <IconButton
                              icon={<CheckIcon />}
                              size="xs"
                              colorScheme="green"
                              onClick={() => handleAssignWorker(worker)}
                              isLoading={loading}
                              aria-label="배정"
                              title="배정"
                              isDisabled={worker.remainingHours <= 0}
                            />
                          </Flex>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </ModalBody>

          <ModalFooter>
            <Button onClick={onClose}>닫기</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 학업시간표 확인 모달 */}
      <Modal 
        isOpen={!!selectedWorker} 
        onClose={handleCloseScheduleView} 
        size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedWorker?.userName}님의 학업시간표
          </ModalHeader>
          <ModalCloseButton />
          
          <ModalBody>
            {viewingSchedule && viewingSchedule.semesterName ? (
              <Box>
                <Box mb={4}>
                  <Text fontWeight="bold">학기: {viewingSchedule.semesterName}</Text>
                  <Text fontSize="sm" color="gray.600">
                    {viewingSchedule.startDate} ~ {viewingSchedule.endDate}
                  </Text>
                </Box>

                <Divider mb={4} />

                <Text fontWeight="bold" mb={2}>
                  근무 예정일: {getDayNameKorean(getDayOfWeek(schedule.workDate))}요일
                </Text>

                <Box mb={4}>
                  <Text fontWeight="bold" mb={2}>해당 요일 수업:</Text>
                  {(() => {
                    const dayName = getDayOfWeek(schedule.workDate);
                    const classes = viewingSchedule.weeklySchedule?.[dayName] || [];
                    
                    if (classes.length === 0) {
                      return (
                        <Box p={3} bg="green.50" borderRadius="md">
                          <Text color="green.700" fontWeight="bold">✓ 수업 없음 (배정 가능)</Text>
                        </Box>
                      );
                    }

                    return (
                      <Box>
                        {classes.map((cls, idx) => (
                          <Box key={idx} p={2} borderWidth="1px" borderRadius="md" mb={2}>
                            <Text fontWeight="bold">{cls.subject}</Text>
                            <Text fontSize="sm">{cls.start} - {cls.end}</Text>
                          </Box>
                        ))}
                      </Box>
                    );
                  })()}
                </Box>

                <Box p={3} bg="blue.50" borderRadius="md">
                  <Text fontWeight="bold" color="blue.700">근무시간</Text>
                  <Text>{schedule.startTime} - {schedule.endTime}</Text>
                </Box>
              </Box>
            ) : (
              <Box textAlign="center" py={8}>
                <Text color="gray.500" fontSize="lg">등록된 학업시간표가 없습니다.</Text>
                <Text fontSize="sm" color="gray.400" mt={2}>
                  근무 예정일: {schedule.workDate} ({getDayNameKorean(getDayOfWeek(schedule.workDate))}요일)
                </Text>
                <Box p={3} bg="blue.50" borderRadius="md" mt={4}>
                  <Text fontWeight="bold" color="blue.700">근무시간</Text>
                  <Text>{schedule.startTime} - {schedule.endTime}</Text>
                </Box>
              </Box>
            )}
          </ModalBody>

          <ModalFooter>
            <Button onClick={handleCloseScheduleView}>닫기</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default WorkerAssignmentModal;
